import { Check, Plus, X } from 'lucide-react'
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
} from 'react'

import { Button } from '@/components/ui/Button'
import { DialogClose, DialogContent, DialogRoot } from '@/components/ui/Dialog'
import {
  useCreateFactoryPreset,
  useDeleteFactoryPreset,
  useFactoryPresets,
  useResolvedChannelPreset,
  type FactoryPreset,
} from '@/api/endpoints/factoryPresets'
import { cn } from '@/lib/cn'
import { errorMessage } from '@/lib/errors'

import styles from './PresetBar.module.scss'

export type PresetParams = Record<string, unknown>

export interface PresetBarProps<TFormValues> {
  /** Backend factory slug — used to scope the presets list (e.g. ``viral_shorts``). */
  factory: string
  /** Active channel slug — needed by the resolved-preset endpoint and to gate
   * the "+ Novo" affordance. */
  channel: string
  /** Snapshot of the current form values — basis of the new-preset payload. */
  formValues: TFormValues
  /** Translate the form snapshot into the JSON payload that lands in
   * ``factory_presets.params``. ``channel`` / ``count`` are stripped
   * server-side, but the caller usually omits them too. */
  formToPayload: (values: TFormValues) => PresetParams
  /** Reverse: payload from a saved preset → form values. Used to hydrate
   * the form whenever the user picks a chip or the channel resolves. */
  payloadToForm: (params: PresetParams) => TFormValues
  /** Apply a preset's values into the form. Called on initial resolve and
   * whenever the user picks a different preset chip. */
  onApplyPreset: (values: TFormValues) => void
}

/**
 * Generic preset selector chip bar. Owns the list of presets for a given
 * factory + the create / select / delete UX. Each consumer form provides
 * its own form↔payload serializers because the form's internal shape
 * (e.g. duration buckets, language enums) typically diverges from the
 * raw backend payload.
 *
 * The bar is purely client-side for selecting / saving — the channel
 * default (the one the cron uses) is set in ``ChannelEditDialog``.
 */
export function PresetBar<TFormValues>({
  factory,
  channel,
  formValues,
  formToPayload,
  payloadToForm,
  onApplyPreset,
}: PresetBarProps<TFormValues>) {
  const presetsQuery = useFactoryPresets(factory)
  const resolvedQuery = useResolvedChannelPreset(channel, factory)
  const create = useCreateFactoryPreset(factory)
  const remove = useDeleteFactoryPreset(factory)

  const presets = useMemo(() => presetsQuery.data ?? [], [presetsQuery.data])
  const resolved = resolvedQuery.data ?? null
  const resolvedId = resolved?.preset?.id ?? null

  const [activeId, setActiveId] = useState<string | null>(resolvedId)
  const [creating, setCreating] = useState(false)
  const [draftName, setDraftName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<FactoryPreset | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)

  // Sync local active to the resolved preset on channel change / resolve.
  useEffect(() => {
    setActiveId(resolvedId)
    if (resolved?.preset) {
      onApplyPreset(payloadToForm(resolved.preset.params))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedId, channel])

  useEffect(() => {
    if (creating) inputRef.current?.focus()
  }, [creating])

  function handleSelect(preset: FactoryPreset) {
    setError(null)
    setActiveId(preset.id)
    onApplyPreset(payloadToForm(preset.params))
  }

  function startCreate() {
    setError(null)
    setDraftName('')
    setCreating(true)
  }

  function cancelCreate() {
    setCreating(false)
    setDraftName('')
    setError(null)
  }

  function commitCreate() {
    const name = draftName.trim()
    if (!name) {
      cancelCreate()
      return
    }
    if (name.length > 80) {
      setError('Máximo 80 caracteres')
      return
    }
    const payload = formToPayload(formValues)
    create.mutate(
      { name, params: payload },
      {
        onSuccess: (preset) => {
          setActiveId(preset.id)
          setCreating(false)
          setDraftName('')
        },
        onError: (err) => {
          setError(errorMessage(err))
        },
      },
    )
  }

  function onInputKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault()
      commitCreate()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancelCreate()
    }
  }

  function requestDelete(preset: FactoryPreset, e: MouseEvent) {
    // Stop the click from bubbling into the chip's activate handler.
    e.stopPropagation()
    setError(null)
    setDeleteTarget(preset)
  }

  function confirmDelete() {
    if (!deleteTarget) return
    const targetId = deleteTarget.id
    remove.mutate(targetId, {
      onSuccess: () => {
        // Backend FK is ON DELETE SET NULL, so the channel falls back to
        // factory_params + default-channel inheritance. Just clear local
        // selection if the deleted preset was the active one.
        if (activeId === targetId) setActiveId(null)
        setDeleteTarget(null)
      },
      onError: (err) => {
        setError(errorMessage(err))
        setDeleteTarget(null)
      },
    })
  }

  return (
    <section className={styles.presetTabs} aria-label="Presets">
      <div className={styles.presetTabsLabel}>Preset</div>
      <div className={styles.presetTabsScroll}>
        {creating ? (
          <div className={styles.prInputWrap}>
            <input
              ref={inputRef}
              type="text"
              className={styles.prInput}
              placeholder="Nome do preset"
              maxLength={80}
              value={draftName}
              onChange={(e) => {
                setDraftName(e.target.value)
                setError(null)
              }}
              onKeyDown={onInputKey}
              disabled={create.isPending}
            />
            <button
              type="button"
              className={styles.prConfirm}
              aria-label="Confirmar"
              onClick={commitCreate}
              disabled={create.isPending}
            >
              <Check aria-hidden />
            </button>
            <button
              type="button"
              className={styles.prCancel}
              aria-label="Cancelar"
              onClick={cancelCreate}
              disabled={create.isPending}
            >
              <X aria-hidden />
            </button>
          </div>
        ) : (
          <button
            type="button"
            className={styles.prNew}
            onClick={startCreate}
            disabled={!channel}
          >
            <Plus aria-hidden /> Novo
          </button>
        )}
        {presets.map((p) => {
          const isActive = p.id === activeId
          return (
            <span key={p.id} className={cn(styles.pr, isActive && styles.prActive)}>
              <button
                type="button"
                className={styles.prLabel}
                onClick={() => {
                  handleSelect(p)
                }}
              >
                {p.name}
              </button>
              <button
                type="button"
                className={styles.prClose}
                aria-label={`Remover preset ${p.name}`}
                onClick={(e) => {
                  requestDelete(p, e)
                }}
                disabled={remove.isPending && deleteTarget?.id === p.id}
              >
                <X aria-hidden />
              </button>
            </span>
          )
        })}
      </div>
      {error ? <span className={styles.presetError}>{error}</span> : null}

      <DialogRoot
        open={deleteTarget !== null}
        onOpenChange={(next) => {
          if (!next && !remove.isPending) setDeleteTarget(null)
        }}
      >
        <DialogContent
          title="Excluir preset"
          description={
            deleteTarget
              ? `Remover "${deleteTarget.name}" não pode ser desfeito. Canais que usam esse preset voltam ao fallback (factory_params ou herança do canal principal).`
              : ''
          }
          footer={
            <>
              <DialogClose asChild>
                <Button variant="secondary" disabled={remove.isPending}>
                  Cancelar
                </Button>
              </DialogClose>
              <Button
                variant="primary"
                onClick={confirmDelete}
                disabled={remove.isPending}
              >
                {remove.isPending ? 'Excluindo...' : 'Excluir'}
              </Button>
            </>
          }
        >
          <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 13 }}>
            Tem certeza que quer excluir este preset?
          </p>
        </DialogContent>
      </DialogRoot>
    </section>
  )
}
